import { useMemo } from "react";

import { ActionBlockButton } from "~/components/buttons/ActionBlockButton";
import Button from "~/components/buttons/Button/Button";
import type {
	MonthlyClosingCloseRequest,
	MonthlyClosingRead,
	MonthlyClosingReopenRequest,
	MonthlyClosingStaffingPart,
} from "~/modules/timeTracking/api/monthlyClosing/monthlyClosingTypes.ts";
import { MonthlyClosingState } from "~/modules/timeTracking/components/components/MonthlyClosingState";

type Props = {
	closing: MonthlyClosingRead;
	onCloseRequest: (request: MonthlyClosingCloseRequest) => void;
	onReopenRequest: (request: MonthlyClosingReopenRequest) => void;
};

type ProjectGroup = {
	projectId: string;
	projectName: string;
	staffings: MonthlyClosingStaffingPart[];
};

const nonProjectLabel = "die Erfassungen ohne Projekt";

function getStaffingLabel(staffing: MonthlyClosingStaffingPart, projectName: string) {
	return `das Staffing „${staffing.orderName}“ im Projekt „${projectName}“`;
}

export function MonthlyClosingPanel(props: Props) {
	// The list comes as the backend sends it, with no filter by activeStaffings: it can hold an
	// inactive staffing that has trackings in the month, and that staffing needs its closing too.
	const projectGroups = useMemo(() => {
		const groups: ProjectGroup[] = [];
		for (const staffing of props.closing.staffings) {
			let group = groups.find((group) => group.projectId === staffing.projectId);
			if (!group) {
				group = { projectId: staffing.projectId, projectName: staffing.projectName, staffings: [] };
				groups.push(group);
			}
			group.staffings.push(staffing);
		}
		return groups;
	}, [props.closing.staffings]);

	return (
		<div className="mt-3 flex flex-col gap-2 text-xs">
			<ul className="flex flex-col gap-1">
				{projectGroups.map((group) => {
					const openStaffingCount = group.staffings.filter((staffing) => !staffing.isClosed).length;
					return (
						<li key={group.projectId}>
							<div className="flex items-center gap-2 font-semibold text-gray-800">
								{group.projectName}
								{openStaffingCount > 1 && (
									<Button theme="white"
											size="xs"
											onClick={() => props.onCloseRequest({
												scope: { scope: "project", projectId: group.projectId },
												label: `alle offenen Staffings des Projekts „${group.projectName}“`,
											})}>
										Projekt schließen
									</Button>
								)}
							</div>
							<ul className="ml-3">
								{group.staffings.map((staffing) => (
									<li key={staffing.staffingId}
										className="flex items-center gap-2">
										<span className="text-gray-700">{staffing.orderName}</span>
										<MonthlyClosingState isClosed={staffing.isClosed} />
										{!staffing.isClosed && (
											<Button theme="none"
													size="xs"
													onClick={() => props.onCloseRequest({
														scope: { scope: "staffings", staffingIds: [staffing.staffingId] },
														label: getStaffingLabel(staffing, group.projectName),
													})}>
												Schließen
											</Button>
										)}
										{staffing.isClosed && staffing.reopen && (
											<ActionBlockButton block={staffing.reopen}
															   theme="none"
															   size="xs"
															   onClick={() => props.onReopenRequest({
																   part: { part: "staffing", staffingId: staffing.staffingId },
																   label: getStaffingLabel(staffing, group.projectName),
															   })}>
												Wieder öffnen
											</ActionBlockButton>
										)}
									</li>
								))}
							</ul>
						</li>
					);
				})}
				<li className="flex items-center gap-2">
					<span className="font-semibold text-gray-800">Ohne Projekt</span>
					<MonthlyClosingState isClosed={props.closing.nonProject.isClosed} />
					{!props.closing.nonProject.isClosed && (
						<Button theme="none"
								size="xs"
								onClick={() => props.onCloseRequest({
									scope: { scope: "nonProject" },
									label: nonProjectLabel,
								})}>
							Schließen
						</Button>
					)}
					{props.closing.nonProject.isClosed && props.closing.nonProject.reopen && (
						<ActionBlockButton block={props.closing.nonProject.reopen}
										   theme="none"
										   size="xs"
										   onClick={() => props.onReopenRequest({
											   part: { part: "nonProject" },
											   label: nonProjectLabel,
										   })}>
							Wieder öffnen
						</ActionBlockButton>
					)}
				</li>
			</ul>
			{!props.closing.isMonthClosed && (
				<div>
					<Button theme="primary"
							size="sm"
							onClick={() => props.onCloseRequest({ scope: { scope: "all" }, label: "den ganzen Monat" })}>
						Monat schließen
					</Button>
				</div>
			)}
		</div>
	);
}
